import { useState } from "react";
import { push, ref, remove, serverTimestamp, set, update } from "firebase/database";
import { Edit3, MailPlus, Plus, Save, Trash2, X } from "lucide-react";
import { database } from "../firebase";
import { mergeEmailTemplates, starterEmailTemplate } from "../emailTemplates";

export default function TemplatesPanel({ templates, user }) {
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(starterEmailTemplate);
  const [busy, setBusy] = useState(false);
  const displayedTemplates = mergeEmailTemplates(templates);

  function startNew() { setEditing("new"); setForm({ ...starterEmailTemplate }); }
  function startEdit(template) {
    setEditing(template.id);
    setForm({ name: template.name, subject: template.subject, cc: template.cc || "", bcc: template.bcc || "", body: template.body });
  }

  async function save(event) {
    event.preventDefault();
    setBusy(true);
    try {
      if (editing === "new") {
        const target = push(ref(database, "admin/emailTemplates"));
        await set(target, { ...form, createdAt: serverTimestamp(), createdBy: user.uid, updatedAt: serverTimestamp(), updatedBy: user.uid });
      } else if (templates.some((template) => template.id === editing)) {
        await update(ref(database, `admin/emailTemplates/${editing}`), { ...form, updatedAt: serverTimestamp(), updatedBy: user.uid });
      } else {
        await set(ref(database, `admin/emailTemplates/${editing}`), { ...form, createdAt: serverTimestamp(), createdBy: user.uid, updatedAt: serverTimestamp(), updatedBy: user.uid });
      }
      setEditing(null);
    } finally {
      setBusy(false);
    }
  }

  async function deleteTemplate(template) {
    if (!window.confirm(`Vorlage „${template.name}“ wirklich löschen?`)) return;
    await remove(ref(database, `admin/emailTemplates/${template.id}`));
  }

  return (
    <main className="templates-page">
      <header className="page-title-row"><div><p className="eyebrow">Outreach-Bibliothek</p><h1>E-Mail-Vorlagen</h1><p>Englische Nachrichten mit automatisch eingesetzten Kontakt- und Vorschaudaten.</p></div><button className="button primary" onClick={startNew}><Plus /> Vorlage erstellen</button></header>
      <div className="variable-help"><strong>Verfügbare Variablen</strong><code>{"{{contactName}}"}</code><code>{"{{companyName}}"}</code><code>{"{{city}}"}</code><code>{"{{previewUrl}}"}</code><code>{"{{senderName}}"}</code></div>
      <div className="template-grid">
        {displayedTemplates.map((template) => (
          <article className="template-card" key={template.id}>
            <div className="template-icon"><MailPlus /></div>
            <p className="eyebrow">{template.builtIn ? "Systemvorlage" : "E-Mail Template"}</p>
            <h2>{template.name}</h2>
            <strong className="template-subject">{template.subject}</strong>
            {template.cc && <span className="template-cc">CC: {template.cc}</span>}
            {template.bcc && <span className="template-bcc">BCC: {template.bcc}</span>}
            <p className="template-excerpt">{template.body}</p>
            <footer><button className="button ghost" onClick={() => startEdit(template)}><Edit3 /> Bearbeiten</button>{template.builtIn ? <span className="built-in-template-note">Systemvorlage · löschgeschützt</span> : <button className="icon-button danger-button" onClick={() => deleteTemplate(template)} aria-label="Vorlage löschen"><Trash2 /></button>}</footer>
          </article>
        ))}
        {templates.length === 0 && <button className="template-empty" onClick={startNew}><Plus /><strong>Eigene Vorlage anlegen</strong><span>Ergänze die Systemvorlagen um eine individuelle Nachricht.</span></button>}
      </div>

      {editing && <div className="modal-backdrop" role="presentation"><section className="drawer template-drawer" role="dialog" aria-modal="true"><header className="drawer-header"><div><p className="eyebrow">Outreach</p><h2>{editing === "new" ? "Neue Vorlage" : "Vorlage bearbeiten"}</h2></div><button className="icon-button" onClick={() => setEditing(null)} aria-label="Schließen"><X /></button></header><form className="drawer-form" onSubmit={save}>
        <label className="field"><span>Name der Vorlage</span><input value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} required /></label>
        <label className="field"><span>Betreff (Englisch)</span><input value={form.subject} onChange={(event) => setForm({ ...form, subject: event.target.value })} required /></label>
        <div className="form-grid"><label className="field"><span>Feste CC-Empfänger</span><input type="email" multiple value={form.cc || ""} onChange={(event) => setForm({ ...form, cc: event.target.value })} placeholder="team@marken-group.com" /></label><label className="field"><span>Feste BCC-Empfänger</span><input type="email" multiple value={form.bcc || ""} onChange={(event) => setForm({ ...form, bcc: event.target.value })} placeholder="rico@markenbreit.com" /></label></div>
        <p className="field-help">Mehrere Adressen mit Komma trennen. Sie werden bei jedem Öffnen dieser Vorlage fest in CC beziehungsweise BCC eingesetzt.</p>
        <label className="field"><span>Nachricht (Englisch)</span><textarea className="template-textarea" value={form.body} onChange={(event) => setForm({ ...form, body: event.target.value })} required /></label>
        <footer className="drawer-actions"><button type="button" className="button ghost" onClick={() => setEditing(null)}>Abbrechen</button><button className="button primary" disabled={busy}><Save /> Speichern</button></footer>
      </form></section></div>}
    </main>
  );
}
